import { Link, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Role } from '@ready/shared';
import { useApi, type Org, type TreeUsage } from '@/lib/api';
import { isManager } from '@/lib/org-roles';
import { ORG_TYPE_LABELS, ROLE_LABELS } from '@/lib/labels';
import { Badge, ErrorText } from '@/components/ui';
import { OrgLogo } from '@/components/org-brand';
import { OrgPageHelp } from '@/components/page-help';

export interface OrgContext {
  org: Org;
  usage: TreeUsage | undefined;
  /** The viewer's own role here; null when viewing as an admin of a parent org. */
  role: Role | null;
  canManage: boolean;
}

export const useOrgContext = () => useOutletContext<OrgContext>();

/**
 * Shell for one organization. Members see Overview and Members; admins also
 * get Invite members and Settings. Child organizations shows when the plan
 * allows them or some exist.
 */
export function OrgLayout() {
  const { orgId = '' } = useParams();
  const api = useApi();
  const org = useQuery({ queryKey: ['org', orgId], queryFn: () => api.org(orgId) });
  const myOrgs = useQuery({ queryKey: ['myOrgs'], queryFn: api.myOrgs });
  const role = myOrgs.data?.orgs.find((o) => o.id === orgId)?.role ?? null;
  const canManage = isManager(role);
  // Admins of this org, or (with no role here) admins of a parent org: they see its
  // members, structure and plan. Learners and authors don't.
  const isAdminView = canManage || (myOrgs.isSuccess && role === null);
  const usage = useQuery({
    queryKey: ['usage', orgId],
    queryFn: () => api.usage(orgId),
    enabled: isAdminView,
  });

  if (org.isPending || myOrgs.isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (org.isError) return <ErrorText error={org.error} />;
  const o = org.data;

  return (
    <div>
      {/* Only for people who can open the parent: its admins, or its own members. */}
      {o.parentId && (role === null || myOrgs.data?.orgs.some((m) => m.id === o.parentId)) && (
        <Link
          to={`/organizations/${o.parentId}`}
          className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Parent organization
        </Link>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <OrgLogo name={o.name} url={o.logoUrl} size="md" />
        <h1 className="text-2xl font-semibold">{o.name}</h1>
        <Badge>{ORG_TYPE_LABELS[o.type]}</Badge>
        {role ? <Badge>{ROLE_LABELS[role]}</Badge> : <Badge>Viewing as parent admin</Badge>}
        <OrgPageHelp type={o.type} />
      </div>
      <div className="pt-6">
        <Outlet context={{ org: o, usage: usage.data, role, canManage } satisfies OrgContext} />
      </div>
    </div>
  );
}
